import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../../hooks/usePlusWebsites';
import { broadcastPropsUpdated } from '../../../../components/preview-messages';
import type { PageDetailsResponse } from './useGetPageDetails';
import { pageDetailsQueryKey } from './useGetPageDetails';

interface SetSectionVisibilityParams {
  sectionId: string;
  isVisible: boolean;
}

function setSectionVisibility(
  websiteId: string,
  pageId: string,
  { sectionId, isVisible }: SetSectionVisibilityParams
) {
  return axiosPrivate
    .put(
      `/v3/pharmacist/plus-websites/${websiteId}/pages/${pageId}/sections/${sectionId}/visibility`,
      {
        isVisible,
      }
    )
    .then((r) => r.data);
}

export function useSetSectionVisibility({
  websiteId,
  pageId,
}: {
  websiteId: string;
  pageId: string;
}) {
  const queryClient = useQueryClient();
  const queryKey = pageDetailsQueryKey(websiteId, pageId);

  return useMutation({
    mutationFn: (params: SetSectionVisibilityParams) =>
      setSectionVisibility(websiteId, pageId, params),
    onMutate: async ({ sectionId, isVisible }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PageDetailsResponse>(queryKey);

      if (previous) {
        const sections = previous.sections.map((section) =>
          section.id === sectionId ? { ...section, isVisible } : section
        );
        queryClient.setQueryData<PageDetailsResponse>(queryKey, { ...previous, sections });
      }

      return { previous };
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: () => markWebsiteAsChanged(queryClient, websiteId),
    onSettled: (_data, _error, { sectionId }) => {
      queryClient.invalidateQueries({ queryKey });
      broadcastPropsUpdated();
    },
    meta: {
      errorMessage: 'Failed to update section visibility',
    },
  });
}
